import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  PrimaryButton,
  DefaultButton,
  Icon,
  TextField,
  ComboBox,
  ChoiceGroup,
} from "@fluentui/react";
import { dir, t } from "i18next";
import "./ReturnReasonCategoryManagement.css";
import themeContext from "../../../context/theme/ThemeContext";
import loaderContext from "../../../context/loader/LoaderContext";
import toastContext from "../../../context/toast/ToastContext";
import userContext from "../../../context/users/UserContext";
import CustomBreadCrumb from "../../../components/CustomBreadCrumb";
import ConfirmModal from "../../../components/Modal/ConfirmModal";import CustomToolTipHost from "../../../components/CustomToolTipHost/CustomToolTipHost";
import { deleteReturnReason, getReturnReasonCategory, saveReturnReason, updateReturnReasonPriority } from "../../../service/MasterService";
import { TOAST_TYPE } from "../../../constants/Constants";

const SubCategory = {
  id: null,
  name: "",
  displayOrder: null
};


const MainCategory = {
  mainId: null,
  mainName: "",
  subCategories: [],
  displayOrder: null
};

const EditingData = {
  mainId: null,
  mainName: "",
  subCategories: [],
  displayOrder: null
};

const ReturnReasonCategoryManagement = () => {
  const { getThemeColor, getAlterColor } = useContext(themeContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const { showToastv2 } = useContext(toastContext);
  const { loadByUsername, authenticated } = useContext(userContext);

  const accentBlue = getThemeColor("active-side-tab");

  const MAX = {
    name: 50
  };

  const requiredStyle = { color: 'red' };

  const [mainCategories, setMainCategories] = useState([]);
  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [backupMainCategory, setBackupMainCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mainCategoryToDelete, setNodeToDelete] = useState(null);

  const selectedMainCategory = mainCategories.find((n) => n.mainId === selectedMainCategoryId);

  useEffect(() => {
    if (!authenticated) loadByUsername();
    init();
  }, [authenticated]);

  const init = async () => {
    setLoaderRunning(true); 
    ref.current.continuousStart();
    try {
        const response = await getReturnReasonCategory();
        console.log("Return Reason Category response:", response);
        setMainCategories(response.data.data || []);
        
    } catch (err) {
        console.error(err);
        showToastv2(TOAST_TYPE.ERROR, "Initial load failed");
    } finally {
        setLoaderRunning(false); ref.current.complete();
    }
  };

  const handleSelectNode = (node) => {
    if (!isEditing) {
      setSelectedMainCategoryId(node.mainId);
    }
  };

  const handleEditClick = (e, node) => {
    e.stopPropagation();
    setSelectedMainCategoryId(node.mainId);
    setEditingData({ 
      mainId: node.mainId,
      mainName: node.mainName,
      subCategories: [...node.subCategories],
      displayOrder: node.displayOrder, });
    setBackupMainCategory(JSON.parse(JSON.stringify(node)));
    setIsEditing(true);
  };

  const handleDeleteClick = (e, node) => {
    e.stopPropagation();
    setNodeToDelete(node);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async() => {
    setLoaderRunning(true); 
    ref.current.continuousStart();
    try {
        const response = await deleteReturnReason(mainCategoryToDelete.mainId);
        console.log("Return Reason Delete response:", response);

        if(response.status == 200 && response.data.result === "SUCCESS") {
            setMainCategories(mainCategories.filter((n) => n.mainId !== mainCategoryToDelete.mainId));
            if (selectedMainCategoryId === mainCategoryToDelete.mainId) {
                setSelectedMainCategoryId(null);
            }
            showToastv2(TOAST_TYPE.SUCCESS, "Return Reason deleted successfully");
        } else {
            showToastv2(TOAST_TYPE.ERROR, "Failed to delete Return Reason");
        }
        
    } catch (err) {
        console.error(err);
        showToastv2(TOAST_TYPE.ERROR, "Failed to delete Return Reason");
    } finally {
        setLoaderRunning(false); ref.current.complete();
        setShowDeleteModal(false);
        setNodeToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setNodeToDelete(null);
  };

  const handleAddNew = () => {
    const maxOrder = Math.max(...mainCategories.map((n) => n.displayOrder || 0), 0);
    console.log("max order", mainCategories, maxOrder);
    const newNode = {
      mainId: null,
      mainName: '',
      subCategories: [],
      displayOrder: maxOrder + 1,
    };
    setMainCategories([...mainCategories, newNode]);
    setSelectedMainCategoryId(newNode.mainId);
    setEditingData({
      mainId: null,
      mainName: '',
      subCategories: [],
      displayOrder: maxOrder + 1,
    });
    setIsEditing(true);
  };

  const validateField = (field, value) => {
    if (field === "mainName") {
      if (!value || !value.trim()) return 'Category name is required.';
      if (value.length > MAX.name)
        return `Category name can have maximum ${MAX.name} characters.`;
      return "";
    }
    if (field === "subName") {
      if (!value || !value.trim()) return "Sub-category name is required.";
      if (value.length > MAX.name)
        return `Sub-category name can have maximum ${MAX.name} characters.`;
      return "";
    }
    return "";
  };

  const handleSave = async () => {
    if (!editingData) return;

    const fieldsToValidate = [{ field: "mainName", value: editingData.mainName }];

    for (const item of fieldsToValidate) {
      const errorMsg = validateField(item.field, item.value);
      if (errorMsg) {
        showToastv2(TOAST_TYPE.INFO, errorMsg);
        return;
      }
    }

    // if (!editingData.subCategories || editingData.subCategories.length === 0) {
    //   showToastv2(TOAST_TYPE.INFO, "At least one sub-category must be added.");
    //   return;
    // }

    for (let i = 0; i < editingData.subCategories.length; i++) {
      const subCategory = editingData.subCategories[i];

      const errorMsg = validateField("subName", subCategory.name);

      if (errorMsg) {
        showToastv2( TOAST_TYPE.INFO, `Sub-category ${i + 1}: ${errorMsg}`);
        return;
      }
    }

    const payloadToSend = {
      mainId: editingData.mainId,
      mainName: editingData.mainName,
      displayOrder: editingData.displayOrder || 0,
      subCategories: editingData.subCategories.map((sub, index) => ({
        id: sub.id,
        name: sub.name,
        displayOrder: sub.displayOrder !== undefined ? sub.displayOrder : index,
      })),
    };

    const isNew = editingData.mainId === null;
    console.log("Saving AI Node:", editingData, "Is new:", isNew);

    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      const response = await saveReturnReason(payloadToSend);
      console.log("Save Return Reason response:", response);

      if (response.status === 200 && response.data.result === "SUCCESS") {
        const savedId = response.data.data;
        const updatedEditingData = {
          mainId: savedId,
          mainName: editingData.mainName,
          subCategories: editingData.subCategories.map((sub, index) => ({
            ...sub,
            displayOrder: sub.displayOrder !== undefined ? sub.displayOrder : index,
          })),
          displayOrder: editingData.displayOrder,
        };
        const updatedNodes = isNew
          ? mainCategories.map((n) => (n.mainid === null ? updatedEditingData : n))
          : mainCategories.map((n) =>
              n.mainId === savedId ? updatedEditingData : n,
            );

        showToastv2(TOAST_TYPE.SUCCESS, `Return reason category saved successfully`);
        setMainCategories(updatedNodes);
        setIsEditing(false);
        setEditingData(null);
        setSelectedMainCategoryId(savedId);
        init();
      }
    } catch (err) {
      console.error(err);
      showToastv2(TOAST_TYPE.ERROR, "Failed to save Return Reason");
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  const handleCancel = () => {
    if (editingData?.mainId === null) {
      setMainCategories(mainCategories.filter(n => n.mainId !== null));
      setSelectedMainCategoryId(null);
    } else {
      setMainCategories(mainCategories.map(n =>
        n.mainId === backupMainCategory.mainId ? backupMainCategory : n
      ));
    }

    setIsEditing(false);
    setEditingData(null);
    setBackupMainCategory(null);
  };

  const updateEditingField = (field, value) => {
    setEditingData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSubCategory = () => {
    if (!editingData) return;
    const maxOrder = Math.max(...editingData.subCategories.map((s) => s.displayOrder || 0), 0);
    updateEditingField('subCategories', [
      ...editingData.subCategories,
      { id: null, name: '', displayOrder: maxOrder + 1 },
    ]);
  };

  const handleUpdateSubCategory = (index, field, value) => {
    if (!editingData) return;
    const updatedSubCategories = [...editingData.subCategories];
    updatedSubCategories[index] = { ...updatedSubCategories[index], [field]: value };
    updateEditingField('subCategories', updatedSubCategories);
  };

  const handleDeleteSubCategory = (index) => {
    if (!editingData) return;
    const updatedSubCategories = editingData.subCategories.filter((_, i) => i !== index);
    updateEditingField('subCategories', updatedSubCategories);
  };

  const handleMoveMainCategory = async (node, direction) => {
    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      const response = await updateReturnReasonPriority(node.mainId, direction);
      console.log("response", response)
      if (response.data.result === "SUCCESS") {
        showToastv2(TOAST_TYPE.SUCCESS, "Order updated successfully");
      } else {
        showToastv2(TOAST_TYPE.ERROR, response.message || "Failed to update order");
      }
    } catch (err) {
      console.error(err);
      showToastv2(TOAST_TYPE.ERROR, "Failed to update order");
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
      init();
    }
  };

  const handleMoveSubCategory = (index, direction) => {
    if (!editingData) return;


    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editingData.subCategories.length) return;

    const updatedSubCategories = [...editingData.subCategories];

    const current = updatedSubCategories[index];
    const neighbor = updatedSubCategories[newIndex];

    const tempOrder = current.displayOrder;
    current.displayOrder = neighbor.displayOrder;
    neighbor.displayOrder = tempOrder;

    [updatedSubCategories[index], updatedSubCategories[newIndex]] = [
      updatedSubCategories[newIndex],
      updatedSubCategories[index],
    ];

    updateEditingField("subCategories", updatedSubCategories);
  };

  const displayMainCategory = isEditing ? editingData : selectedMainCategory;
  const empty = mainCategories.length === 0;

  return (
    <div
      className="ai-nodes-container"
    >
      <div className="breadCrumb">
        <CustomBreadCrumb items={[{ text: t("Return Reason Category Management") }]} />
      </div>

      {empty && (
        <div className="empty-node-wrapper">
          <p>{t("No return reason categories configured yet")}</p>
          <PrimaryButton
            text={t("Add New Category")}
            disabled={isEditing}
            onClick={handleAddNew}
          />
        </div>
      )}
      {!empty && (
        <>
          <div className="toolbar">
            <PrimaryButton onClick={handleAddNew} disabled={isEditing}>
              Add New Category
            </PrimaryButton>
          </div>
          <div className="split-container">
            <div className="left-panel">
              <table className="nodes-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%', textAlign: 'left' }}>Order</th>
                    <th style={{ width: '70%', textAlign: 'left' }}>Category Name</th>
                    <th style={{ width: '20%', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mainCategories.map((node, index) => {
                    const isSelected = node.mainId === selectedMainCategoryId;
                    const isThisEditing = isEditing && isSelected;
                  
                    return (
                      <tr
                        key={node.id}
                        className={isEditing ? "disable-hover" : ""}
                        onClick={() => handleSelectNode(node)}
                        style={{
                          background: isSelected ? accentBlue : "transparent",
                        }}
                      >
                        <td>
                          {!isEditing && (
                            <div className="order-controls">
                              <CustomToolTipHost content="Move Up">
                                <Icon
                                  iconName="ChevronUp"
                                  className={index === 0 ? 'disabled-icon' : ''}
                                  styles={{
                                    root: {
                                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                                      fontSize: 12,
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (index !== 0) handleMoveMainCategory(node, 'up');
                                  }}
                                />
                              </CustomToolTipHost>
                              <CustomToolTipHost content="Move Down">
                                <Icon
                                  iconName="ChevronDown"
                                  className={
                                    index === mainCategories.length - 1 ? 'disabled-icon' : ''
                                  }
                                  styles={{
                                    root: {
                                      cursor:
                                        index === mainCategories.length - 1
                                          ? 'not-allowed'
                                          : 'pointer',
                                      fontSize: 12,
                                      marginLeft: 10
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (index !== mainCategories.length - 1)
                                      handleMoveMainCategory(node, 'down');
                                  }}
                                />
                              </CustomToolTipHost>
                            </div>
                          )}
                        </td>
                        <td>
                          {isThisEditing ? (
                            <TextField
                              name="mainName"
                              value={editingData?.mainName || ""}
                              onChange={(e) =>
                                updateEditingField("mainName", e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Enter category name"
                            />
                          ) : (
                            <CustomToolTipHost content={node.mainName ||" No name"}>
                                <div className="single-line-ellipsis">
                                   {node.mainName || (
                                    <span className="placeholder">New Category</span>
                                    )}
                                </div>
                            </CustomToolTipHost>
                            
                          )}
                        </td>
                        
                        <td>
                          {!isThisEditing && (
                            <div className="actions">
                                <CustomToolTipHost content= "Edit">
                                    <Icon 
                                        className={`edit-icon ${isEditing ? "disabled-icon" : ""}`}
                                        iconName="Edit"
                                        styles={{
                                        root: {
                                            cursor: isEditing
                                            ? "not-allowed"
                                            : "pointer",
                                            marginRight: 8,
                                        },
                                        }}
                                        onClick={(e) => isEditing ? null : handleEditClick(e, node)}
                                    />
                                </CustomToolTipHost>
                                <CustomToolTipHost content= "Delete">
                                    <Icon
                                        className={`delete-icon ${isEditing ? "disabled-icon" : ""}`}
                                        iconName="Delete"
                                        styles={{
                                        root: {
                                            cursor: isEditing
                                            ? "not-allowed"
                                            : "pointer",
                                        },
                                        }}
                                        onClick={(e) => isEditing ? null : handleDeleteClick(e, node)}
                                    />
                                </CustomToolTipHost>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="right-panel" style={{ background: accentBlue }}>
              {displayMainCategory ? (
                <>
                <div className="top-details-container">
                  <div className="sub-category-section">
                    <div className="rules-header">
                      <h3>Sub-category Configuration</h3>
                    </div>

                    {displayMainCategory.subCategories.length === 0 ? (
                      <div className="no-rules">No sub-categories configured</div>
                    ) : (
                      <div className="modal-scroll rules-table-wrapper">
                        <table className="rules-table2">
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', width: '10%' }}>Order</th>
                              <th style={{ textAlign: "left", width: "82%" }}>Sub-category Name
                                <span style={requiredStyle}>*</span>
                              </th>
                              <th style={{ textAlign: "left" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayMainCategory.subCategories.map((rule, idx) => (
                              <tr key={idx}>
                                {isEditing ? (
                                  <>
                                    <td>
                                        <div className="order-controls">
                                          <CustomToolTipHost content="Move Up">
                                            <Icon
                                              iconName="ChevronUp"
                                              className={idx === 0 ? 'disabled-icon' : ''}
                                              styles={{
                                                root: {
                                                  cursor: idx === 0 ? 'not-allowed' : 'pointer',
                                                  fontSize: 12,
                                                },
                                              }}
                                              onClick={() => {
                                                if (idx !== 0) handleMoveSubCategory(idx, 'up');
                                              }}
                                            />
                                          </CustomToolTipHost>
                                          <CustomToolTipHost content="Move Down">
                                            <Icon
                                              iconName="ChevronDown"
                                              className={
                                                idx ===
                                                (editingData?.subCategories.length || 0) - 1
                                                  ? 'disabled-icon'
                                                  : ''
                                              }
                                              styles={{
                                                root: {
                                                  cursor:
                                                    idx ===
                                                    (editingData?.subCategories.length || 0) - 1
                                                      ? 'not-allowed'
                                                      : 'pointer',
                                                  fontSize: 12,
                                                  marginLeft: 10
                                                },
                                              }}
                                              onClick={() => {
                                                if (
                                                  idx !==
                                                  (editingData?.subCategories.length || 0) - 1
                                                )
                                                  handleMoveSubCategory(idx, 'down');
                                              }}
                                            />
                                          </CustomToolTipHost>
                                        </div>
                                      </td>
                                    <td style={{ width: "25%" }}>
                                      <TextField
                                        value={rule.name}
                                        onChange={(e, value) =>
                                          handleUpdateSubCategory(
                                            idx,
                                            "name",
                                            value
                                          )
                                        }
                                        placeholder="Sub-category Name"
                                      />
                                    </td>
                                    
                                    <td style={{ textAlign: "center" }}>
                                        <CustomToolTipHost content = "Delete">
                                            <Icon
                                                className="delete-icon"
                                                iconName="Delete"
                                                styles={{ root: { cursor: "pointer" } }}
                                                onClick={(e) => handleDeleteSubCategory(idx)}
                                            />
                                      </CustomToolTipHost>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{idx + 1}</td>
                                    <td>{rule.name || "-"}</td>
                                    <td>-</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                {isEditing && (
                    <div className="action-buttons-strip">
                      <PrimaryButton onClick={handleAddSubCategory}>
                        + Add Sub-category
                      </PrimaryButton>
                      <div className="button-group-left">
                        <DefaultButton onClick={handleCancel}>
                          Cancel
                        </DefaultButton>
                        <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-selection">
                  <p>Select a return reason category from the left panel to view details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
            handleConfirm={handleConfirmDelete}
            handleNotConfirm={handleCancelDelete}
            showConfirmModal={showDeleteModal}
            message={t('Are you sure you want to delete this return reason category?')}
            title={t('Confirm Delete')}
            confirmButtonText={t("confirm-message.yes")}
        />
      )}
    </div>
  );
};

export default ReturnReasonCategoryManagement;